import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  ChevronsUpDown,
  FileText,
  Pause,
  Pencil,
  Play,
  Plus,
  Trash2,
} from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import {
  ListingDataTableCard,
  ListingDataTableScroll,
} from '@/components/ui/listingDataTable'
import { PageShell } from '@/components/layout/PageShell'
import { ListingTableLoadingRow, ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { primaryEntityActionButtonClass } from '@/components/ui/entityActionButtonClasses'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@lib/utils/helpers'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import styles from '../styles/reportsSchedulerTable.module.css'
import { ReportScheduleModal } from '../components/ReportScheduleModal'
import { DEFAULT_REPORT_SCHEDULE_LIST_PARAMS, EMPTY_MESSAGE, STATUS_LABEL } from '../constants'
import {
  deleteReportSchedule,
  getReportCatalog,
  getReportScheduleList,
  saveReportSchedule,
  updateReportScheduleStatus,
} from '../services/reportsSchedulerService'
import type {
  ReportCatalog,
  ReportSchedule,
  ReportScheduleListParams,
  ReportScheduleSortKey,
  ReportScheduleSubmitPayload,
  ReportsSchedulerConfirmTarget,
} from '../types'
import {
  formatScheduleDate,
  getReportLabel,
} from '../utils/reportsSchedulerFormatters'

export function ReportsSchedulerPage() {
  const [schedules, setSchedules] = useState<ReportSchedule[]>([])
  const [reportCatalog, setReportCatalog] = useState<ReportCatalog>({})
  const [params, setParams] = useState<ReportScheduleListParams>(DEFAULT_REPORT_SCHEDULE_LIST_PARAMS)
  const [pageOptions, setPageOptions] = useState({ current: 1, last: 1, total: 0 })
  const [loading, setLoading] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ReportSchedule | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState<ReportsSchedulerConfirmTarget | null>(null)
  const mountedRef = useRef(false)
  const isMobile = useIsMobile()
  // Matches Angular / peer config modules: table on desktop, card grid on mobile only.
  const isListView = !isMobile

  const loadSchedules = useCallback(async (nextParams: ReportScheduleListParams) => {
    setLoading(true)
    try {
      const response = await getReportScheduleList(nextParams)
      setSchedules(response.data)
      setPageOptions({
        current: response.current,
        last: response.last,
        total: response.total,
      })
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadCatalog = useCallback(async () => {
    try {
      setReportCatalog(await getReportCatalog())
    } catch (error) {
      showApiError(error)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void loadSchedules(DEFAULT_REPORT_SCHEDULE_LIST_PARAMS)
    void loadCatalog()
  }, [loadCatalog, loadSchedules])

  const applyParams = useCallback(
    (next: ReportScheduleListParams) => {
      setParams(next)
      void loadSchedules(next)
    },
    [loadSchedules],
  )

  const sort = (orderBy: ReportScheduleSortKey) => {
    applyParams({
      ...params,
      page: 1,
      orderBy,
      sortBy: params.sortBy === 'asc' ? 'desc' : 'asc',
    })
  }

  const openAdd = () => {
    setEditTarget(null)
    setModalOpen(true)
  }

  const openEdit = (schedule: ReportSchedule) => {
    setEditTarget(schedule)
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditTarget(null)
  }

  const handleSubmit = async (payload: ReportScheduleSubmitPayload, scheduleId?: number) => {
    setSubmitting(true)
    try {
      await saveReportSchedule(payload, scheduleId)
      showSuccess(scheduleId ? 'Scheduler updated successfully!' : 'Scheduler added successfully!')
      closeModal()
      void loadSchedules(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const confirmAction = async () => {
    if (!confirmTarget) return
    try {
      if (confirmTarget.type === 'status') {
        await updateReportScheduleStatus(confirmTarget.schedule.rs_id, confirmTarget.nextStatus)
        showSuccess(`Schedule status changed to ${confirmTarget.nextStatus} successfully!`)
      } else {
        await deleteReportSchedule(confirmTarget.schedule.rs_id)
        showSuccess('Schedule Deleted Successfully!')
      }
      setConfirmTarget(null)
      void loadSchedules(params)
    } catch (error) {
      showApiError(error)
    }
  }

  const viewHistory = (schedule: ReportSchedule) => {
    const returnTo = `${window.location.pathname}${window.location.search}`
    const query = new URLSearchParams({
      q: schedule.email_subject ?? '',
      returnTo,
    }).toString()
    if (window.location.hostname === 'cornerstone.libertyrent.com') {
      window.location.assign(`/reports/mailgun-logs?${query}`)
      return
    }
    window.location.assign(`/mailtrap?${query}`)
  }

  /** Inactive columns show ChevronsUpDown, active column shows ▲ or ▼ */
  const SortArrows = ({ column }: { column: ReportScheduleSortKey }) => {
    if (params.orderBy === column) {
      return (
        <span className={styles.sortArrowActive}>
          {params.sortBy === 'asc' ? '▲' : '▼'}
        </span>
      )
    }
    return <ChevronsUpDown className={cn('inline h-3.5 w-3.5 shrink-0', styles.sortArrowInactive)} aria-hidden />
  }

  const confirmDialogCopy = useMemo(() => {
    if (!confirmTarget) return { title: '', message: '' }
    if (confirmTarget.type === 'status') {
      return {
        title: 'Change Schedule Status',
        message: 'Do you want to change this Schedule Status?',
      }
    }
    return {
      title: 'Delete Schedule',
      message: 'Do you want to Delete this Schedule?',
    }
  }, [confirmTarget])

  const renderActions = (schedule: ReportSchedule) => (
    <div className={styles.actionsWrapper}>
      <button
        type="button"
        onClick={() => openEdit(schedule)}
        className={cn(styles.actionButton, styles.actionButtonEdit)}
        aria-label="Edit schedule"
        title="Edit"
        data-testid={`reports-scheduler-edit-${schedule.rs_id}`}
      >
        <Pencil className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => viewHistory(schedule)}
        className={cn(styles.actionButton, styles.actionButtonHistory)}
        aria-label="View history"
        title="View History"
        data-testid={`reports-scheduler-history-${schedule.rs_id}`}
      >
        <FileText className="h-4 w-4" />
      </button>
      {schedule.report_status === 'RUNNING' ? (
        <button
          type="button"
          onClick={() => setConfirmTarget({ type: 'status', schedule, nextStatus: 'PAUSED' })}
          className={cn(styles.actionButton, styles.actionButtonPause)}
          aria-label="Pause schedule"
          title="Pause"
          data-testid={`reports-scheduler-pause-${schedule.rs_id}`}
        >
          <Pause className="h-4 w-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setConfirmTarget({ type: 'status', schedule, nextStatus: 'RUNNING' })}
          className={cn(styles.actionButton, styles.actionButtonResume)}
          aria-label="Resume schedule"
          title="Resume"
          data-testid={`reports-scheduler-resume-${schedule.rs_id}`}
        >
          <Play className="h-4 w-4" />
        </button>
      )}
      <button
        type="button"
        onClick={() => setConfirmTarget({ type: 'delete', schedule })}
        className={cn(styles.actionButton, styles.actionButtonDelete)}
        aria-label="Delete schedule"
        title="Delete"
        data-testid={`reports-scheduler-delete-${schedule.rs_id}`}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )

  const statusBadge = (schedule: ReportSchedule) => (
    <span
      className={
        schedule.report_status === 'RUNNING'
          ? styles.statusBadgeRunning
          : styles.statusBadgePaused
      }
    >
      {STATUS_LABEL[schedule.report_status] ?? schedule.report_status}
    </span>
  )

  return (
    <PageShell
      title="REPORTS SCHEDULER"
      contentClassName="min-w-0"
      pageHeaderTestIdRoot="reports-scheduler-page-header"
      headerAction={
        <button
          type="button"
          onClick={openAdd}
          className={cn(primaryEntityActionButtonClass, 'w-full sm:w-auto')}
          data-testid="reports-scheduler-add-button"
        >
          <Plus className="h-4 w-4" />
          Add Schedule
        </button>
      }
    >
      <div className="min-w-0" data-testid="reports-scheduler-page">
        <ListingDataTableCard data-testid="reports-scheduler-results-card">
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => applyParams({ ...params, page })}
            onPerPageChange={(perPage) => applyParams({ ...params, perPage, page: 1 })}
            testIdPrefix="reports-scheduler"
            summaryLayout="angular"
            showPerPageSelect={false}
            barDataTestId="reports-scheduler-pagination-header-bar"
          />

          {isListView ? (
            <ListingDataTableScroll data-testid="reports-scheduler-table-wrapper">
              <table className={styles.listingTable} data-testid="reports-scheduler-table">
                <thead>
                  <tr>
                    <th scope="col">
                      <button
                        type="button"
                        onClick={() => sort('friendly_name')}
                        className={cn(
                          styles.sortButton,
                          params.orderBy === 'friendly_name' && styles.sortButtonActive,
                        )}
                      >
                        <span>Friendly Name</span>
                        <SortArrows column="friendly_name" />
                      </button>
                    </th>
                    <th scope="col">
                      <button
                        type="button"
                        onClick={() => sort('report_name')}
                        className={cn(
                          styles.sortButton,
                          params.orderBy === 'report_name' && styles.sortButtonActive,
                        )}
                      >
                        <span>Report</span>
                        <SortArrows column="report_name" />
                      </button>
                    </th>
                    <th scope="col" className={styles.centerHeader}>
                      <span>PMCs</span>
                    </th>
                    <th scope="col" className={styles.centerHeader}>
                      <button
                        type="button"
                        onClick={() => sort('last_sent_date')}
                        className={cn(
                          styles.sortButton,
                          params.orderBy === 'last_sent_date' && styles.sortButtonActive,
                        )}
                      >
                        <span>Last Sent</span>
                        <SortArrows column="last_sent_date" />
                      </button>
                    </th>
                    <th scope="col" className={styles.centerHeader}>
                      <button
                        type="button"
                        onClick={() => sort('next_send_date')}
                        className={cn(
                          styles.sortButton,
                          params.orderBy === 'next_send_date' && styles.sortButtonActive,
                        )}
                      >
                        <span>Next Send</span>
                        <SortArrows column="next_send_date" />
                      </button>
                    </th>
                    <th scope="col" className={styles.centerHeader}>
                      <button
                        type="button"
                        onClick={() => sort('report_status')}
                        className={cn(
                          styles.sortButton,
                          params.orderBy === 'report_status' && styles.sortButtonActive,
                        )}
                      >
                        <span>Status</span>
                        <SortArrows column="report_status" />
                      </button>
                    </th>
                    <th scope="col" className={styles.centerHeader}>
                      <span>Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <ListingTableLoadingRow colSpan={7} />
                  ) : schedules.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={styles.emptyCell}>
                        {EMPTY_MESSAGE}
                      </td>
                    </tr>
                  ) : (
                    schedules.map((schedule) => (
                      <tr key={schedule.rs_id} data-testid={`reports-scheduler-row-${schedule.rs_id}`}>
                        <td className={styles.nameCell}>{schedule.friendly_name}</td>
                        <td>{getReportLabel(reportCatalog, schedule.report_name)}</td>
                        <td className={styles.centerCell}>{schedule.pmc_count ?? '-'}</td>
                        <td className={styles.centerCell}>
                          {formatScheduleDate(schedule.last_sent_date)}
                        </td>
                        <td className={styles.centerCell}>
                          {formatScheduleDate(schedule.next_send_date)}
                        </td>
                        <td className={styles.centerCell}>{statusBadge(schedule)}</td>
                        <td className={styles.actionsCell}>{renderActions(schedule)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </ListingDataTableScroll>
          ) : (
            <div
              className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 sm:p-4 xl:grid-cols-3"
              data-testid="reports-scheduler-mobile-list"
            >
              {loading ? (
                <div className="col-span-full">
                  <ModuleScreenLoading
                    variant="inline"
                    className="py-8"
                    data-testid="reports-scheduler-mobile-loading"
                  />
                </div>
              ) : schedules.length === 0 ? (
                <p className="col-span-full py-8 text-center text-sm text-slate-500">
                  {EMPTY_MESSAGE}
                </p>
              ) : (
                schedules.map((schedule) => (
                  <article
                    key={schedule.rs_id}
                    className="min-w-0 space-y-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                    data-testid={`reports-scheduler-card-${schedule.rs_id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-500">Friendly Name</p>
                      <h3 className="break-words text-sm font-medium text-slate-900">
                        {schedule.friendly_name}
                      </h3>
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-slate-500">Report</p>
                      <p className="break-words text-sm text-slate-700">
                        {getReportLabel(reportCatalog, schedule.report_name)}
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500">PMCs</p>
                        <p className="text-sm text-slate-700">{schedule.pmc_count ?? '-'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500">Status</p>
                        <div className="mt-0.5">{statusBadge(schedule)}</div>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500">Last Sent</p>
                        <p className="text-sm text-slate-700">
                          {formatScheduleDate(schedule.last_sent_date)}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-slate-500">Next Send</p>
                        <p className="text-sm text-slate-700">
                          {formatScheduleDate(schedule.next_send_date)}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end border-t border-slate-100 pt-2">
                      {renderActions(schedule)}
                    </div>
                  </article>
                ))
              )}
            </div>
          )}

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={params.perPage}
            onPageChange={(page) => applyParams({ ...params, page })}
            onPerPageChange={(perPage) => applyParams({ ...params, perPage, page: 1 })}
            testIdPrefix="reports-scheduler"
            summaryLayout="angular"
            barDataTestId="reports-scheduler-pagination-footer-bar"
          />
        </ListingDataTableCard>
      </div>

      <ReportScheduleModal
        isOpen={modalOpen}
        schedule={editTarget}
        reportCatalog={reportCatalog}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        isOpen={confirmTarget !== null}
        title={confirmDialogCopy.title}
        message={confirmDialogCopy.message}
        primaryLabel="Yes"
        onPrimary={confirmAction}
        secondaryLabel="No"
        onSecondary={() => setConfirmTarget(null)}
      />
    </PageShell>
  )
}
